<div class="custom-report-page">

  <!-- ── Top toolbar ─────────────────────────────────────────────────────── -->
  <div class="report-toolbar">
    <div class="toolbar-left">
      <h5 class="toolbar-title">
        Custom Report Builder
      </h5>
      <span class="badge-columns" *ngIf="selectedCount > 0">
        {{ selectedCount }} column{{ selectedCount !== 1 ? 's' : '' }} selected
      </span>
    </div>

    <div class="view-toolbar-actions d-flex align-items-center" style="gap: 12px;">

      <!-- Client type / activity (current client state): OR within group, AND between groups -->
      <app-tablet-status-filter
        [options]="statusFilterOptions"
        [selectedValues]="selectedStatusOptions"
        (valuesChange)="onStatusFilter($event)">
      </app-tablet-status-filter>

      <!-- Collection period: applies only to Collection Data fields -->
      <div class="d-flex align-items-center" style="gap: 6px;">
        <span style="font-size: 12px; color: #64748b; white-space: nowrap;">Collection period</span>
        <app-tablet-date-filter
          [showLastThreeMonths]="true"
          [value]="dateFilterValue"
          [disabled]="!hasCollectionFields"
          disabledTitle="Select a Collection Data field to filter by collection dates"
          (filterChange)="onCollectionDateFilter($event)">
        </app-tablet-date-filter>
      </div>

      <!-- Clear column filters (visible even when they produce zero rows) -->
      <button *ngIf="columnFilters.length > 0" type="button" class="btn btn-sm btn-outline-secondary"
        style="white-space: nowrap; border-radius: 8px; font-size: 13px; padding: 5px 12px;"
        (click)="clearColumnFilters()" title="Clear column filters">
        <i class="fa fa-filter"></i> Clear column filters
      </button>
      
      <!-- Export Excel (Official System Style) -->
      <button class="btn btn-sm d-flex align-items-center gap-1"
        style="background-color: #217346; color: white; box-shadow: none !important; border: none; white-space: nowrap; border-radius: 8px; font-size: 13px; padding: 5px 12px;"
        (click)="exportExcel()" [disabled]="!hasResults && !selectedCount" title="Export current report as Excel">
        <i class="fa fa-file-excel-o"></i>
        <span>Export Excel</span>
      </button>

      <!-- Action Group (Official System Style) -->
      <div class="view-switcher rounded-lg" role="group">
        
        <!-- Saved Configs toggle -->
        <button type="button" class="view-switcher__btn" [class.view-switcher__btn--active]="showSavedPanel" style="position: relative;" (click)="showSavedPanel = !showSavedPanel" title="Saved Reports">
          <i class="fa fa-bookmark"></i><span>Saved Reports</span>
          <span class="badge-count" *ngIf="savedConfigs.length > 0">{{ savedConfigs.length }}</span>
        </button>

        <!-- Save current config -->
        <button type="button" class="view-switcher__btn" (click)="openSaveModal()" [disabled]="!selectedCount" title="Save View">
          <i class="fa fa-save"></i><span>Save View</span>
        </button>

        <!-- Clear All -->
        <button type="button" class="view-switcher__btn" (click)="clearAll()" [disabled]="!selectedCount" title="Clear All">
          <i class="fa fa-eraser"></i><span>Clear</span>
        </button>

        <!-- Toggle Panel -->
        <button type="button" class="view-switcher__btn" (click)="panelOpen = !panelOpen" title="Toggle column selector">
          <span style="font-size: 16px; line-height: 1;">&#9776;</span>
        </button>
        
      </div>
    </div>
  </div>

  <!-- ── Feedback message ─────────────────────────────────────────────────── -->
  <div *ngIf="feedbackMsg" class="feedback-bar" [ngClass]="'feedback-' + feedbackType">
    <i [attr.data-feather]="feedbackType === 'success' ? 'check-circle' : feedbackType === 'error' ? 'alert-circle' : 'info'"></i>
    {{ feedbackMsg }}
  </div>

  <!-- ── Main layout ──────────────────────────────────────────────────────── -->
  <div class="report-layout" [class.panel-collapsed]="!panelOpen">

    <!-- LEFT PANEL: Column selector ──────────────────────────────────────── -->
    <aside class="columns-panel" *ngIf="panelOpen">

      <!-- Run button at top -->
      <div class="panel-run-btn">
        <button class="btn-run" (click)="runReport(1)" [disabled]="!selectedCount || loading">
          <i data-feather="play"></i>
          {{ loading ? 'Loading...' : 'Run Report' }}
        </button>
      </div>

      <!-- Field groups -->
      <div class="field-group" *ngFor="let group of fieldGroups">

        <div class="group-header" (click)="toggleGroup(group)">
          <div class="group-title">
            <i [attr.data-feather]="group.icon"></i>
            <span>{{ group.label }}</span>
          </div>
          <div class="group-actions">
            <span class="group-count" [class.has-selected]="getSelectedCount(group) > 0">
              {{ getSelectedCount(group) }} / {{ group.fields.length }}
            </span>
            <button class="btn-select-all" (click)="selectAll(group); $event.stopPropagation()" title="Toggle all">
              <i data-feather="check-square"></i>
            </button>
            <i [attr.data-feather]="group.expanded ? 'chevron-up' : 'chevron-down'" class="chevron"></i>
          </div>
        </div>

        <div class="field-list" *ngIf="group.expanded">
          <label class="field-item" *ngFor="let field of group.fields"
            [class.selected]="field.selected"
            [class.field-locked]="field.locked"
            (click)="toggleField(field)">
            <span class="field-checkbox">
              <span class="checkbox-inner" [class.checked]="field.selected">
                <i *ngIf="field.locked" class="fa fa-lock" style="font-size:10px;"></i>
                <i data-feather="check" *ngIf="field.selected && !field.locked"></i>
              </span>
            </span>
            <span class="field-label">{{ field.label }}</span>
            <span *ngIf="field.locked" class="locked-badge">Required</span>
          </label>
        </div>
      </div>
    </aside>

    <!-- RIGHT: Grid area ──────────────────────────────────────────────────── -->
    <div class="grid-area">

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!showGrid && !loading">
        <div class="empty-icon">
          <i data-feather="bar-chart-2"></i>
        </div>
        <h6>Build your report</h6>
        <p>Select the columns you need from the left panel,<br>then click <strong>Run Report</strong>.</p>
        <button class="btn-run-empty" (click)="runReport(1)" [disabled]="!selectedCount">
          <i data-feather="play"></i> Run Report
        </button>
      </div>

      <!-- Loading -->
      <div class="loading-state" *ngIf="loading">
        <div class="spinner"></div>
        <span>Loading data...</span>
      </div>

      <!-- AG Grid Integration -->
      <div class="grid-wrapper" *ngIf="showGrid">
        <app-kite-grid
          [columnDefs]="columnDefs"
          [rowData]="gridData"
          [loading]="paging"
          [gridOptions]="gridOptions"
          [totalRecords]="totalRecords"
          [pageSize]="pageSize"
          (pageChange)="goToPage($event)"
          (gridReady)="onGridReady($event)">
        </app-kite-grid>
      </div>
    </div>
  </div>

  <!-- ── Saved Reports Sidebar ─────────────────────────────────────────────── -->
  <div class="saved-overlay" *ngIf="showSavedPanel" (click)="showSavedPanel = false"></div>
  <aside class="saved-panel" [class.open]="showSavedPanel">
    <div class="saved-panel-header">
      <h6><i class="fa fa-bookmark"></i> Saved Reports</h6>
      <button class="btn-ghost" (click)="showSavedPanel = false">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="saved-empty" *ngIf="!savedConfigs.length">
      <i class="fa fa-inbox fa-2x"></i>
      <p>No saved reports yet.<br>Build a report and click "Save View".</p>
    </div>

    <div class="saved-item" *ngFor="let config of savedConfigs" (click)="loadConfig(config)">
      <div class="saved-item-info">
        <span class="saved-item-name">{{ config.name }}</span>
        <span class="saved-item-meta">
          {{ config.selectedColumns.length }} columns
        </span>
      </div>
      <button class="btn-delete-config" (click)="deleteConfig(config, $event)" title="Delete report" style="color: #dc3545;">
        <i class="fa fa-trash"></i>
      </button>
    </div>
  </aside>

  <!-- ── Save Config Modal ─────────────────────────────────────────────────── -->
  <div class="modal-overlay" *ngIf="saveModalOpen" (click)="saveModalOpen = false">
    <div class="save-modal" (click)="$event.stopPropagation()">
      <div class="save-modal-header">
        <h4 class="modal-title text-white">Save Report View</h4>
        <button type="button" class="btn text-white" aria-label="Close" (click)="saveModalOpen = false"><i class="fa fa-times fa-lg"></i></button>
      </div>
      <div class="save-modal-body">
        <label class="modal-label">Report Name</label>
        <input
          type="text"
          class="modal-input"
          [(ngModel)]="saveConfigName"
          placeholder="e.g. Monthly Performance Report"
        />
        <div class="modal-checkbox-wrapper mt-3">
          <input
            type="checkbox"
            id="share-config"
            [(ngModel)]="saveConfigShared"
          />
          <label for="share-config">Share with team members</label>
        </div>
      </div>
      <div class="save-modal-footer">
        <button class="btn-cancel" (click)="saveModalOpen = false">Cancel</button>
        <button class="btn-save" (click)="saveConfig()" [disabled]="!saveConfigName.trim()">
          Save View
        </button>
      </div>
    </div>
  </div>

</div>
